import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BACKEND_URL } from "../../utils/config";
import { handleFetchResponse } from "./helpers";
import "./Report.css";

// Mock data until a backend is available
const mockReports = [
    { id: 1, title: "Q4 2025 Patient Care Summary", content: "This report details the patient care metrics for the fourth quarter of 2025. Overall satisfaction remains high, with notable improvements in response times." },
    { id: 2, title: "November 2025 Staffing Report", content: "Staffing levels were optimal throughout November. Overtime was kept to a minimum, and all shifts were fully covered." },
    { id: 3, title: "Annual Performance Review 2025", content: "The annual performance review indicates a 15% increase in operational efficiency. Key achievements include the successful integration of the new scheduling system." },
    { id: 4, title: "Client Feedback Analysis - H2 2025", content: "The second half of 2025 saw a significant increase in positive client feedback. The most praised aspect was the compassionate care provided by our staff." }
];

export default function ReportsView({ fetchUrl = "/reports" }) {
    const [reports, setReports] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showSidebar, setShowSidebar] = useState(true);
    const navigate = useNavigate();
    
    // Filter states
    const [usernameFilter, setUsernameFilter] = useState('');
    const [pswUsernameFilter, setPswUsernameFilter] = useState('');
    const [startDateFilter, setStartDateFilter] = useState('');
    const [endDateFilter, setEndDateFilter] = useState('');
    
    // Photo states
    const [showPhotosModal, setShowPhotosModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [photos, setPhotos] = useState([]);
    const [photosLoading, setPhotosLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [imageNames, setImageNames] = useState({});
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(null);
    
    // Create report states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [createError, setCreateError] = useState(null);
    const [createSuccess, setCreateSuccess] = useState(null);
    
    // Delete report states
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [isAdminOrPsw, setIsAdminOrPsw] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [userOptions, setUserOptions] = useState([]);
    const [pswOptions, setPswOptions] = useState([]);
    const [fetchingOptions, setFetchingOptions] = useState(false);
    const [formData, setFormData] = useState({
        user_id: '',
        psw_id: '',
        report_date: new Date().toISOString().split('T')[0],
        title: '',
        location: '',
        description: '',
        services: [],
        comments: ''
    });

    // Services by category
    const servicesCategories = {
        'Memory Care Services': [
            'Cognitive stimulation activities',
            'Memory exercises',
            'Safe environment monitoring',
            'Routine maintenance',
            'Wandering prevention',
            'Family communication and updates',
            'Medication management'
        ],
        'Hygiene Care Services': [
            'Assistance with showering and bathing',
            'Hair washing and care',
            'Oral hygiene assistance',
            'Foot care and maintenance',
            'Bedding changes and care'
        ],
        'Grooming Care Services': [
            'Outfit selection and coordination',
            'Dressing assistance',
            'Hair styling and grooming',
            'Skincare assistance'
        ],
        'Meal Care Services': [
            'Personalized meal planning',
            'Grocery shopping assistance',
            'Meal preparation',
            'Eating assistance',
            'Custom cooking',
            'Kitchen cleanup and organization'
        ],
        'Health Care Services': [
            'Medication reminders and monitoring',
            'Medication organization and management',
            'Detailed medication administration records',
            'Safe mobility assistance',
            'Physical therapy exercise support',
            'Medical appointment accompaniment'
        ],
        'Light Housekeeping Care Services': [
            'Making beds',
            'Cleaning bathrooms',
            'Sweeping and mopping floors',
            'Managing trash and recycling',
            'Handling laundry',
            'Organizing mail, bills, and documents'
        ],
        'Throughout the Day Care Service': [
            'Toileting and personal care assistance',
            'Hydration monitoring and support',
            'Regular vital signs monitoring',
            'Gentle exercise guidance',
            'Engaging activities and games',
            'Meaningful conversation and companionship',
            'Technology guidance and support',
            'Dedicated companionship care'
        ],
        'Live-in / Overnight Care Service': [
            'Regular safety monitoring',
            'Nutritious snack preparation',
            'Snack and meal assistance',
            'Hydration management',
            'Health monitoring and documentation',
            'Safe nighttime mobility assistance',
            'Overnight companionship',
            '24/7 emergency response'
        ]
    };

//...
    useEffect(() => {
        const fetchReports = async () => {
            try {
                const res = await fetch(`${BACKEND_URL}${fetchUrl}`, {
                    credentials: 'include'
                });
                
                const checkedRes = await handleFetchResponse(res, navigate);
                if (!checkedRes) return;
                
                if (!checkedRes.ok) throw new Error("Failed to fetch reports.");
                
                try {
                    const data = await checkedRes.json();
                    setReports(data.reports);
                } catch (jsonError) {
                    console.error('Error parsing JSON:', jsonError);
                    throw new Error('Received an invalid response from the server.');
                }
                
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchReports();
        
        // Initialize user_id from localStorage and check if admin
        const role = localStorage.getItem('role');
        const user_id = localStorage.getItem('user_id');
        const username = localStorage.getItem('username');
        setUserRole(role);
        if (user_id) {
            setFormData(prev => ({
                ...prev,
                user_id: user_id
            }));
        }
        if (role === 'admin' || role === 'psw') {
            setIsAdminOrPsw(true);
        }
    }, [fetchUrl]);

    const handleReportSelect = (report) => {
        setSelectedReport(report);
    };
    
    const handleViewPhotos = async () => {
        if (!selectedReport) return;
        
        setPhotosLoading(true);
        setShowPhotosModal(true);
        setPhotos([]);
        
        try {
            const res = await fetch(`${BACKEND_URL}/reports/images/${selectedReport.report_id}`, {
                method: 'GET',
                credentials: 'include'
            });

            if (res.status === 401) {
                setPhotos([]);
                navigate(`/${BACKEND_URL}/login`);
                return;
            }
            
            // Handle 404 directly without redirect
            if (res.status === 404) {
                setPhotos([]);
                return;
            }
            
            const checkedRes = await handleFetchResponse(res, navigate);
            if (!checkedRes) {
                setPhotos([]);
                return;
            }
            
            if (checkedRes.ok) {
                try {
                    const data = await checkedRes.json();
                    if (data.images && Array.isArray(data.images)) {
                        // Convert base64 images to data URLs
                        const processedImages = data.images.map((photo) => ({
                            name: photo.image_name || 'Unknown',
                            url: `data:image/jpeg;base64,${photo.image}`,
                            uploaded_at: photo.uploaded_at
                        }));
                        setPhotos(processedImages);
                    } else {
                        setPhotos([]);
                    }
                } catch (jsonError) {
                    console.error('Error parsing JSON:', jsonError);
                    setPhotos([]);
                }
            } else {
                setPhotos([]);
            }
        } catch (err) {
            console.error('Error fetching photos:', err);
            setPhotos([]);
        } finally {
            setPhotosLoading(false);
        }
    };
    
    const handleOpenUploadModal = () => {
        setShowUploadModal(true);
        setSelectedFiles([]);
        setImageNames({});
        setUploadError(null);
        setUploadSuccess(null);
    };
    
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        setSelectedFiles(files);
        const newNames = {};
        files.forEach((file, index) => {
            newNames[index] = '';
        });
        setImageNames(newNames);
        setUploadError(null);
    };
    
    const handleImageNameChange = (index, name) => {
        setImageNames({
            ...imageNames,
            [index]: name
        });
    };
    
    const handleDownloadImage = (photo) => {
        const link = document.createElement('a');
        link.href = photo.url;
        link.download = photo.name || 'image';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    
    const handleOpenCreateModal = () => {
        setShowCreateModal(true);
        setCreateError(null);
        setCreateSuccess(null);
        
        // If user is PSW, lock psw_id to current user
        if (userRole === 'psw') {
            const user_id = localStorage.getItem('user_id');
            setFormData(prev => ({
                ...prev,
                psw_id: user_id || ''
            }));
        }
        
        fetchUserAndPswOptions();
    };
    
    const fetchUserAndPswOptions = async () => {
        setFetchingOptions(true);
        try {
            const [usersRes, pswRes] = await Promise.all([
                fetch(`${BACKEND_URL}/admin/users`, { credentials: 'include' }),
                fetch(`${BACKEND_URL}/admin/psws`, { credentials: 'include' })
            ]);
            
            if (usersRes.ok) {
                const usersData = await usersRes.json();
                setUserOptions(usersData.users || []);
            }
            if (pswRes.ok) {
                const pswData = await pswRes.json();
                setPswOptions(pswData.users || []);
            }
        } catch (err) {
            console.error('Error fetching options:', err);
        } finally {
            setFetchingOptions(false);
        }
    };
    
    const handleCreateFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleServiceChange = (service) => {
        setFormData(prev => {
            const services = prev.services.includes(service)
                ? prev.services.filter(s => s !== service)
                : [...prev.services, service];
            return {
                ...prev,
                services
            };
        });
    };
    
    const handleCreateReport = async () => {
        if (!formData.user_id || !formData.report_date) {
            setCreateError('Please fill in all required fields (User and Date)');
            return;
        }
        
        setCreateLoading(true);
        setCreateError(null);
        setCreateSuccess(null);
        
        try {
            const dateTime = `${formData.report_date}T00:00:00Z`;
            const reportPayload = {
                user_id: formData.user_id,
                psw_id: formData.psw_id || null,
                service: null,
                report_date: dateTime,
                title: formData.title || null,
                location: formData.location || null,
                description: formData.description || null,
                services: formData.services.length > 0 ? formData.services : null,
                comments: formData.comments || null
            };
            
            const res = await fetch(`${BACKEND_URL}/reports`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(reportPayload)
            });
            
            if (!res.ok) {
                setCreateError('Failed to create report. Please try again.');
                return;
            }
            
            setCreateSuccess('Report created successfully!');
            setFormData({
                user_id: '',
                psw_id: '',
                report_date: new Date().toISOString().split('T')[0],
                title: '',
                location: '',
                description: '',
                services: [],
                comments: ''
            });
            
            setTimeout(() => {
                setShowCreateModal(false);
                setCreateSuccess(null);
                // Refresh reports
                window.location.reload();
            }, 1500);
        } catch (err) {
            console.error('Error creating report:', err);
            setCreateError(err.message || 'Failed to create report');
        } finally {
            setCreateLoading(false);
        }
    };

    const handleDeleteReport = async () => {
        if (!selectedReport) return;

        if (!window.confirm(`Are you sure you want to delete the report "${selectedReport.title}"? This action cannot be undone.`)) {
            return;
        }

        setDeleteLoading(true);
        setDeleteError(null);

        try {
            const res = await fetch(`${BACKEND_URL}/reports/${selectedReport.report_id}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!res.ok) {
                throw new Error('Failed to delete report');
            }

            setDeleteLoading(false);
            // Refresh reports and deselect
            setSelectedReport(null);
            window.location.reload();
        } catch (err) {
            console.error('Error deleting report:', err);
            setDeleteError(err.message || 'Failed to delete report');
            setDeleteLoading(false);
        }
    };
    
    const handleUploadPhotos = async () => {
        if (!selectedReport || selectedFiles.length === 0) {
            setUploadError('Please select at least one image');
            return;
        }
        
        setUploadLoading(true);
        setUploadError(null);
        setUploadSuccess(null);
        
        try {
            const formData = new FormData();
            selectedFiles.forEach((file, index) => {
                formData.append('images', file);
                const imageName = imageNames[index] || file.name;
                formData.append('imageNames', imageName);
            });
            
            const res = await fetch(`${BACKEND_URL}/reports/images/${selectedReport.report_id}`, {
                method: 'PUT',
                credentials: 'include',
                body: formData
            });
            
            // Handle errors without redirect for image upload
            if (!res.ok) {
                setUploadError('Failed to upload photos. Please try again.');
                return;
            }
            
            setUploadSuccess('Photos uploaded successfully!');
            setSelectedFiles([]);
            setTimeout(() => {
                setShowUploadModal(false);
                setUploadSuccess(null);
            }, 1500);
        } catch (err) {
            console.error('Error uploading photos:', err);
            setUploadError(err.message || 'Failed to upload photos');
        } finally {
            setUploadLoading(false);
        }
    };

    // Filter reports based on all active filters
    const filteredReports = reports.filter(report => {
        // Filter by user_username
        if (usernameFilter && !report.user_username?.toLowerCase().includes(usernameFilter.toLowerCase())) {
            return false;
        }
        
        // Filter by psw_username
        if (pswUsernameFilter && !report.psw_username?.toLowerCase().includes(pswUsernameFilter.toLowerCase())) {
            return false;
        }
        
        // Filter by date range
        if (startDateFilter || endDateFilter) {
            const reportDate = new Date(report.created_at);
            if (startDateFilter) {
                const startDate = new Date(startDateFilter);
                if (reportDate < startDate) return false;
            }
            if (endDateFilter) {
                const endDate = new Date(endDateFilter);
                endDate.setHours(23, 59, 59, 999);
                if (reportDate > endDate) return false;
            }
        }
        
        return true;
    });

    if (loading) {
        return <div className="loading-message">Loading reports...</div>;
    }

    if (error) {
        return <div className="error-message">Error: {error}</div>;
    }

    return (
        <div className="reports-page">
            <aside className="reports-sidebar" style={{ display: showSidebar ? 'block' : 'none' }}>
                <h2>Reports</h2>
                
                {/* Filter Section */}
                <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '6px', marginBottom: '16px' }}>
                    <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Filters</h3>
                    
                    <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Patient Username</label>
                        <input
                            type="text"
                            placeholder="Search patient..."
                            value={usernameFilter}
                            onChange={(e) => setUsernameFilter(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '4px',
                                fontSize: '12px',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>PSW Username</label>
                        <input
                            type="text"
                            placeholder="Search service worker..."
                            value={pswUsernameFilter}
                            onChange={(e) => setPswUsernameFilter(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '4px',
                                fontSize: '12px',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Start Date</label>
                        <input
                            type="date"
                            value={startDateFilter}
                            onChange={(e) => setStartDateFilter(e.target.value)}
                            min="2000-01-01"
                            max="2099-12-31"
                            style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '4px',
                                fontSize: '12px',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>End Date</label>
                        <input
                            type="date"
                            value={endDateFilter}
                            onChange={(e) => setEndDateFilter(e.target.value)}
                            min="2000-01-01"
                            max="2099-12-31"
                            style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '4px',
                                fontSize: '12px',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                    
                    {(usernameFilter || pswUsernameFilter || startDateFilter || endDateFilter) && (
                        <button
                            onClick={() => {
                                setUsernameFilter('');
                                setPswUsernameFilter('');
                                setStartDateFilter('');
                                setEndDateFilter('');
                            }}
                            style={{
                                width: '100%',
                                padding: '6px 8px',
                                background: '#e2e8f0',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '500',
                                cursor: 'pointer'
                            }}
                        >
                            Clear Filters
                        </button>
                    )}
                </div>
                
                <ul className="reports-list" style={{ paddingBottom: '6vh' }}>
                    {filteredReports.length > 0 ? (
                        filteredReports.map(report => (
                            <li 
                                key={report.report_id || report.id} 
                                className={`report-item ${selectedReport?.report_id === report.report_id || selectedReport?.id === report.id ? 'selected' : ''}`}
                                onClick={() => handleReportSelect(report)}
                            >
                                {report.title}
                            </li>
                        ))
                    ) : (
                        <li style={{ padding: '12px', color: '#64748b', fontSize: '12px', textAlign: 'center' }}>
                            No reports match filters
                        </li>
                    )}
                </ul>
            </aside>
            <main className="report-content-area">
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '16px' }}>
                    <button
                        onClick={handleOpenCreateModal}
                        style={{
                            padding: '8px 16px',
                            background: '#8b5cf6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'background 0.3s',
                            display: isAdminOrPsw ? 'block' : 'none'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#7c3aed'}
                        onMouseLeave={(e) => e.target.style.background = '#8b5cf6'}
                    >
                        ➕ Create Report
                    </button>
                </div>
                {selectedReport ? (
                    <div className="report-content">
                        <h1>{selectedReport.title}</h1>
                        <div className="report-details">
                            <p><strong>Patient:</strong> {selectedReport.user_username || 'N/A'}</p>
                            <p><strong>Caregiver:</strong> {selectedReport.psw_username || 'N/A'}</p>
                            <p><strong>Created:</strong> {new Date(selectedReport.created_at).toLocaleString()}</p>
                            <p><strong>Last Updated:</strong> {new Date(selectedReport.updated_at).toLocaleString()}</p>
                        </div>
                        <p>{selectedReport.description}</p>

                        {/* Services Display */}
                        {selectedReport.services && selectedReport.services.length > 0 && (
                            <div style={{ marginTop: '20px', padding: '16px', background: '#f8f9fa', borderRadius: '6px' }}>
                                <h3 style={{ marginTop: 0, marginBottom: '12px', color: '#1e293b' }}>Services Provided:</h3>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {selectedReport.services.map((service, index) => (
                                        <span
                                            key={index}
                                            style={{
                                                display: 'inline-block',
                                                padding: '6px 12px',
                                                background: '#8b5cf6',
                                                color: 'white',
                                                borderRadius: '20px',
                                                fontSize: '12px',
                                                fontWeight: '500'
                                            }}
                                        >
                                            {service}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Comments Display */}
                        {selectedReport.comments && (
                            <div style={{ marginTop: '20px', padding: '16px', background: '#f0f9ff', borderRadius: '6px' }}>
                                <h3 style={{ marginTop: 0, marginBottom: '8px', color: '#1e293b' }}>Comments:</h3>
                                <p style={{ color: '#334155', margin: 0, whiteSpace: 'pre-wrap' }}>{selectedReport.comments}</p>
                            </div>
                        )}
                        
                        <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
                            <button
                                onClick={handleViewPhotos}
                                style={{
                                    padding: '10px 20px',
                                    background: '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    transition: 'background 0.3s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#2563eb'}
                                onMouseLeave={(e) => e.target.style.background = '#3b82f6'}
                            >
                                📷 View Photos
                            </button>
                            <button
                                onClick={handleOpenUploadModal}
                                style={{
                                    padding: '10px 20px',
                                    background: '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    transition: 'background 0.3s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#059669'}
                                onMouseLeave={(e) => e.target.style.background = '#10b981'}
                            >
                                ⬆️ Upload Photos
                            </button>
                            <button
                                onClick={handleDeleteReport}
                                disabled={deleteLoading}
                                style={{
                                    padding: '10px 20px',
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: deleteLoading ? 'not-allowed' : 'pointer',
                                    transition: 'background 0.3s',
                                    opacity: deleteLoading ? 0.7 : 1
                                }}
                                onMouseEnter={(e) => !deleteLoading && (e.target.style.background = '#dc2626')}
                                onMouseLeave={(e) => !deleteLoading && (e.target.style.background = '#ef4444')}
                            >
                                {deleteLoading ? '🗑️ Deleting...' : '🗑️ Delete Report'}
                            </button>
                        </div>
                        {deleteError && (
                            <div style={{ marginTop: '12px', color: '#ef4444', padding: '12px', background: '#fee', borderRadius: '4px' }}>
                                Error: {deleteError}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="report-placeholder">
                        <p>Select a report from the list to view its content.</p>
                    </div>
                )}
            </main>
            
            {/* View Photos Modal */}
            {showPhotosModal && (
                <div className="modal-overlay" onClick={() => setShowPhotosModal(false)}>
                    <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Report Photos</h2>
                            <button className="modal-close" onClick={() => setShowPhotosModal(false)} aria-label="Close modal">
                                ×
                            </button>
                        </div>
                        <div className="modal-form" style={{ maxHeight: 'calc(90vh - 120px)', overflowY: 'auto', padding: '20px' }}>
                            {photosLoading ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <div className="loading-spinner" style={{ margin: '0 auto 20px' }}></div>
                                    <p style={{ color: '#64748b' }}>Loading photos...</p>
                                </div>
                            ) : photos.length > 0 ? (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
                                    {photos.map((photo, index) => (
                                        <div key={index} style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                            <img
                                                src={photo.url}
                                                alt={photo.name}
                                                style={{ width: '100%', height: '200px', objectFit: 'cover', cursor: 'pointer' }}
                                                onClick={() => handleDownloadImage(photo)}
                                                title="Click to download"
                                            />
                                            <div style={{ padding: '8px', fontSize: '12px', color: '#64748b', background: '#f8f9fa' }}>
                                                <p style={{ margin: '0 0 4px 0', fontWeight: '500' }}>{photo.name}</p>
                                                {photo.uploaded_at && (
                                                    <p style={{ margin: 0 }}>
                                                        {new Date(photo.uploaded_at).toLocaleDateString()}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                                    <p>No photos available for this report.</p>
                                </div>
                            )}
                        </div>
                        <div className="modal-actions">
                            <button
                                type="button"
                                className="modal-button-cancel"
                                onClick={() => setShowPhotosModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Upload Photos Modal */}
            {showUploadModal && (
                <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
                    <div className="modal-content" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Upload Photos</h2>
                            <button className="modal-close" onClick={() => setShowUploadModal(false)} aria-label="Close modal">
                                ×
                            </button>
                        </div>
                        <div className="modal-form" style={{ padding: '20px' }}>
                            {uploadError && (
                                <div style={{ color: '#ef4444', padding: '12px', background: '#fee', borderRadius: '4px', marginBottom: '16px' }}>
                                    {uploadError}
                                </div>
                            )}
                            {uploadSuccess && (
                                <div style={{ color: '#10b981', padding: '12px', background: '#d1fae5', borderRadius: '4px', marginBottom: '16px' }}>
                                    {uploadSuccess}
                                </div>
                            )}
                            <div style={{ marginBottom: '16px' }}>
                                <label htmlFor="photo-upload" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#1e293b' }}>
                                    Select Photos:
                                </label>
                                <input
                                    id="photo-upload"
                                    type="file"
                                    accept=".jpg,.jpeg,.png"
                                    multiple
                                    onChange={handleFileSelect}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '2px dashed #cbd5e1',
                                        borderRadius: '6px',
                                        cursor: 'pointer'
                                    }}
                                />
                                {selectedFiles.length > 0 && (
                                    <div style={{ marginTop: '16px' }}>
                                        <p style={{ fontSize: '14px', fontWeight: '500', marginBottom: '12px', color: '#1e293b' }}>
                                            {selectedFiles.length} file(s) selected:
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            {selectedFiles.map((file, index) => (
                                                <div key={index} style={{ padding: '12px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                                    <p style={{ fontSize: '12px', fontWeight: '500', marginBottom: '6px', color: '#64748b' }}>
                                                        {file.name}
                                                    </p>
                                                    <input
                                                        type="text"
                                                        placeholder="Image name (optional)"
                                                        value={imageNames[index] || ''}
                                                        onChange={(e) => handleImageNameChange(index, e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '8px',
                                                            border: '1px solid #cbd5e1',
                                                            borderRadius: '4px',
                                                            fontSize: '12px',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button
                                type="button"
                                className="modal-button-cancel"
                                onClick={() => setShowUploadModal(false)}
                                disabled={uploadLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="modal-button-submit"
                                onClick={handleUploadPhotos}
                                disabled={uploadLoading || selectedFiles.length === 0}
                            >
                                {uploadLoading ? 'Uploading...' : 'Upload'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Create Report Modal */}
            {showCreateModal && isAdminOrPsw &&(
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Create Report</h2>
                            <button className="modal-close" onClick={() => setShowCreateModal(false)} aria-label="Close modal">
                                ×
                            </button>
                        </div>
                        <div className="modal-form" style={{ padding: '20px', maxHeight: 'calc(90vh - 120px)', overflowY: 'auto' }}>
                            {createError && (
                                <div style={{ color: '#ef4444', padding: '12px', background: '#fee', borderRadius: '4px', marginBottom: '16px' }}>
                                    {createError}
                                </div>
                            )}
                            {createSuccess && (
                                <div style={{ color: '#10b981', padding: '12px', background: '#d1fae5', borderRadius: '4px', marginBottom: '16px' }}>
                                    {createSuccess}
                                </div>
                            )}
                            
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#1e293b' }}>
                                    Patient *
                                </label>
                                <select
                                    name="user_id"
                                    value={formData.user_id}
                                    onChange={handleCreateFormChange}
                                    disabled={fetchingOptions}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box',
                                        cursor: fetchingOptions ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    <option value="">Select a patient</option>
                                    {userOptions.map((user) => (
                                        <option key={user.user_id} value={user.user_id}>
                                            {user.username}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            {userRole !== 'psw' && (
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#1e293b' }}>
                                        Service Worker
                                    </label>
                                    <select
                                        name="psw_id"
                                        value={formData.psw_id}
                                        onChange={handleCreateFormChange}
                                        disabled={fetchingOptions}
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            boxSizing: 'border-box',
                                            cursor: fetchingOptions ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        <option value="">Select a service worker (optional)</option>
                                        {pswOptions.map((psw) => (
                                            <option key={psw.user_id} value={psw.user_id}>
                                                {psw.username}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#1e293b' }}>
                                    Report Date *
                                </label>
                                <input
                                    type="date"
                                    name="report_date"
                                    value={formData.report_date}
                                    onChange={handleCreateFormChange}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                            
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#1e293b' }}>
                                    Title
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleCreateFormChange}
                                    placeholder="Report Title"
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                            
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#1e293b' }}>
                                    Location
                                </label>
                                <input
                                    type="text"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleCreateFormChange}
                                    placeholder="Location"
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                            
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#1e293b' }}>
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleCreateFormChange}
                                    placeholder="Report Description"
                                    rows="4"
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box',
                                        fontFamily: 'inherit',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>

                            {/* Services Checkboxes */}
                            {Object.keys(servicesCategories).map((category) => (
                                <div key={category} style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: '#1e293b' }}>
                                        {category}
                                    </label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {servicesCategories[category].map((service) => (
                                            <div key={service} style={{ display: 'flex', alignItems: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    id={`service-${service}`}
                                                    checked={formData.services.includes(service)}
                                                    onChange={() => handleServiceChange(service)}
                                                    style={{
                                                        marginRight: '8px',
                                                        cursor: 'pointer',
                                                        width: '16px',
                                                        height: '16px'
                                                    }}
                                                />
                                                <label
                                                    htmlFor={`service-${service}`}
                                                    style={{
                                                        cursor: 'pointer',
                                                        fontSize: '14px',
                                                        color: '#334155'
                                                    }}
                                                >
                                                    {service}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#1e293b' }}>
                                    Comments
                                </label>
                                <textarea
                                    name="comments"
                                    value={formData.comments}
                                    onChange={handleCreateFormChange}
                                    placeholder="Additional comments or notes"
                                    rows="4"
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box',
                                        fontFamily: 'inherit',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button
                                type="button"
                                className="modal-button-cancel"
                                onClick={() => setShowCreateModal(false)}
                                disabled={createLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="modal-button-submit"
                                onClick={handleCreateReport}
                                disabled={createLoading}
                            >
                                {createLoading ? 'Creating...' : 'Create Report'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Floating Sidebar Toggle Button */}
            <button 
                onClick={() => setShowSidebar(!showSidebar)}
                style={{
                    position: 'fixed',
                    bottom: '24px',
                    left: '24px',
                    padding: '12px 16px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background 0.3s, transform 0.2s',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
                onMouseEnter={(e) => {
                    e.target.style.background = '#5a6268';
                    e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                    e.target.style.background = '#6c757d';
                    e.target.style.transform = 'scale(1)';
                }}
                title={showSidebar ? 'Hide sidebar' : 'Show sidebar'}
            >
                {showSidebar ? '◀ Hide Sidebar' : '▶ Show Sidebar'}
            </button>
            
            {/* Floating Back Button */}
            <button 
                onClick={() => navigate(-1)}
                style={{
                    position: 'fixed',
                    bottom: '24px',
                    left: '240px',
                    padding: '12px 16px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background 0.3s, transform 0.2s',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
                onMouseEnter={(e) => {
                    e.target.style.background = '#5a6268';
                    e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                    e.target.style.background = '#6c757d';
                    e.target.style.transform = 'scale(1)';
                }}
                title="Go back"
            >
                ← Back
            </button>
        </div>
    );
}
